import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LoginSchema } from 'shared';
import type { LoginInput } from 'shared';

import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { FormField } from '../components/ui/FormField';
import { useLogin } from '../features/auth/mutations';
import { safeNextPath } from '../lib/navigation/safeNextPath';

export function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = safeNextPath(searchParams.get('next'));
  const login = useLogin();
  const form = useForm<LoginInput>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = form.handleSubmit((values) => {
    login.mutate(values, {
      onSuccess: () => navigate(next),
    });
  });

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg px-4 py-8 text-text">
      <Card
        className="w-full max-w-md"
        header={
          <div>
            <h1 className="text-2xl font-semibold">Log in</h1>
            <p className="mt-1 text-sm text-text-muted">
              Open the gate before the trade reaches you.
            </p>
          </div>
        }
      >
        <form className="space-y-4" onSubmit={onSubmit}>
          <FormField
            label="Email"
            type="email"
            autoComplete="email"
            error={form.formState.errors.email?.message}
            {...form.register('email')}
          />
          <FormField
            label="Password"
            type="password"
            autoComplete="current-password"
            error={form.formState.errors.password?.message}
            {...form.register('password')}
          />
          {login.isError ? <p className="text-sm text-danger">Invalid email or password.</p> : null}
          <Button className="w-full" size="lg" type="submit" disabled={login.isPending}>
            {login.isPending ? 'Logging in...' : 'Log in'}
          </Button>
          <p className="text-center text-sm text-text-muted">
            Need an account?{' '}
            <Link className="text-primary hover:underline" to="/register">
              Register
            </Link>
          </p>
        </form>
      </Card>
    </main>
  );
}
